import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { createTenant, getTenantDetailsThunk, getTenantPetApplication } from "./thunks";
import { AgentApprovedApplicationData, AgentApprovedApplications, AgentApprovedLandlord, AgentApprovedPets, DynamicItem, StageOneDataResponse, StageOneSubmitResponse, TenantBasicDetails, TenantPetsData, TenantPetsDocument, TenantPetsSupportDocuments } from "./types";
import { secureLocalStorage } from "../../utils/secureStorage";

interface TenantState {
  data: null,
  primary_tenant_id: string;
  inProgress?: boolean;
  error?: string | null;
  propertyId?: string;
  primaryTenants?: Array<TenantBasicDetails>;
  applicationid?: string;
  stageOneFullData?: StageOneDataResponse;
  // tenantPets?: TenantPetsData[];
  tenantPets?: TenantPetsData[];
  tenantPetsDocuments?: TenantPetsDocument[];
  tenantName?: string;
  tenantAddress?: string;
  applicationReceivedOn?: string;
  tenantPropertyAddress?: string;
  isValidAgent?: boolean;
  agentId?: string;
  agentFormId?: string;
  teantLink?: string;
  uniqueLink?: string;
  applicationStatus?: string;
  agentApprovedResponse?: AgentApprovedApplicationData;
  agentApprovedApplications?: AgentApprovedApplications;
  agentApprovedlandlord?: AgentApprovedLandlord;
  agentApprovedPetsDetails?: AgentApprovedPets[];
  dayLeftToAutoComplete?: number; 
  agentApprovedApplicationId?: string;
  agentAprovedTenants?: Array<TenantBasicDetails>;
  landlordApplicationStatus?: string;
  tenantAgentId?: string;
  tenantFormId?: string;
  tenantApplicationStatus?: string;
  agentName?: string;
  agentCompany?: string;
  agentLogo?: string;
  agentApprovedPets?: TenantPetsData[];
  support_documents_count?: number;
  support_documents?: TenantPetsSupportDocuments[];
  tenantItems?: DynamicItem[];
  landlordAgentId?: string;
  landlordAgentLink?: string;

}

const initialState: TenantState = {
  primary_tenant_id: "",
  data: null,
  error: null,
  applicationStatus: "",
  agentApprovedApplicationId: "",
  agentAprovedTenants: [],
  landlordApplicationStatus: "",
  tenantApplicationStatus: "",
  agentName: "",
  agentCompany: "",
  agentLogo: ""
};

const tenantSlice = createSlice({
  name: "tenant",
  initialState,
  reducers: {
    setPrimartTenant(state, action: PayloadAction<StageOneSubmitResponse>) {
      state.primary_tenant_id = action.payload.data.tenant_link;
      state.propertyId = action.payload.data.property.id;
      state.primaryTenants = action.payload.data.tenants;
      state.applicationid = action.payload.data.application_id;
      state.teantLink = action.payload.data.tenant_link;
      state.uniqueLink = action.payload.data.uniqiue_link;
    },
    setStageOneData(state, action: PayloadAction<StageOneDataResponse>) {
      secureLocalStorage.setItem("agent_name", action.payload?.data.agent?.name || "");
      secureLocalStorage.setItem("agent_company", action.payload.data?.agent?.company || "");
      state.stageOneFullData = action.payload;
      state.primary_tenant_id = action.payload.data.property.tenants[0]?.id || "";
      state.propertyId = action.payload.data.property.id;
      state.primaryTenants = action.payload.data.property.tenants.map(t => ({
        ...t,
        name: `${t.first_name} ${t.last_name}`,
        secure_link: t.unique_link_token // or generate as needed
      }));
      state.tenantPets = action.payload.data.property.pets || [];
      state.tenantAgentId = action.payload.data.property.agent_id || "";
      state.tenantFormId = action.payload.data.property.form_id || "";
      state.tenantApplicationStatus = action.payload.data.application.status || "";
      state.agentName = action.payload.data?.agent?.name || "";
      state.agentCompany = action.payload.data?.agent?.company || "";

      const tenantLists = action.payload.data.property.tenants;
      const tenantNames = tenantLists.map(tenant => `${tenant.first_name} ${tenant.last_name}`).join(', ');
      secureLocalStorage.setItem("tenantNames", tenantNames);
      
    },
    setTenantDetails(state, action: PayloadAction<StageOneSubmitResponse>) {
      state.tenantName = action.payload?.data.tenant?.name || "";
      state.tenantAddress = action.payload?.data.property?.address || "";
    },
    setTenantSubmittedData(state, action: PayloadAction<any>) {
      state.tenantPets = action.payload.data.property.pets || [];
      state.primaryTenants = action.payload.data.property.tenants.map((t: any) => ({
        ...t,
        name: `${t.first_name} ${t.last_name}`,
        secure_link: t.unique_link_token // or generate as needed
      }));
      state.applicationReceivedOn = action.payload.data.application?.submitted_at   || "";
      state.tenantPropertyAddress = action.payload.data.property?.address || "";
      state.propertyId = action.payload.data.property.id;
      state.applicationStatus = action.payload.data.application.status || "";
      state.dayLeftToAutoComplete = action.payload.data.application?.days_left || 0;

      // When agent click save after that we need to display the save info in the fields
      state.agentApprovedApplications = action.payload.data.application || [];
      state.agentApprovedPetsDetails = action.payload.data.property.pets || [];
      state.agentApprovedlandlord = action.payload.data.landlord || {}
    },
    setIsValidAgent(state, action: PayloadAction<boolean>) {
      state.isValidAgent = action.payload;
    },
    setAgentId(state, action: PayloadAction<string>) {
      state.agentId = action.payload;
    },
    setFormId(state, action: PayloadAction<string>) {
      state.agentFormId = action.payload;
    },
    setAgentNameAndCompany(state, action: PayloadAction<{name: string; company: string; logo?: string}>) {
      state.agentName = action.payload.name;
      state.agentCompany = action.payload.company;
      state.agentLogo = action.payload.logo ?? "";
    },
    setApprovedPetsDetails(state, action: PayloadAction<AgentApprovedApplicationData>) {
      state.agentApprovedResponse = action.payload;
      state.agentApprovedApplications = action.payload.data?.properties?.[0].application || [];
      state.agentApprovedPetsDetails = action.payload.data?.properties?.[0]?.property?.pets || [];
      state.agentApprovedlandlord = action.payload.data?.properties?.[0]?.landlord || {}
      state.agentApprovedApplicationId = action.payload.data?.properties?.[0].application?.id || "";
      state.agentAprovedTenants = action.payload.data.properties?.[0]?.property?.tenants || [];
      state.tenantPropertyAddress = action.payload.data.properties?.[0]?.property?.address || "";
      state.dayLeftToAutoComplete = action.payload.data?.properties?.[0].application?.days_left || 0;
      state.landlordApplicationStatus = action.payload.data?.properties?.[0].application?.status || "";
      state.agentApprovedPets = (action.payload.data?.properties?.[0].property?.pets || []).map((pet: any) => ({
        ...pet,
        landlord_comments: pet.landlord_comments ?? "",
        approval_conditions: pet.approval_conditions ?? "",
        pet_number: pet.pet_number ?? "",
        property_id: pet.property_id ?? ""
      }));
      state.tenantAgentId = action.payload.data?.properties?.[0]?.agent?.id || "";
      state.landlordAgentId = action.payload.data?.properties?.[0]?.agent?.id || "";
      state.landlordAgentLink = action.payload.data?.properties?.[0]?.application?.agent_link || "";
      secureLocalStorage.setItem("agent_name", action.payload.data?.properties?.[0]?.agent?.name || "");
      secureLocalStorage.setItem("agent_company", action.payload.data?.properties?.[0]?.agent?.company || "");
    },
    /**
     * Sets the items array in the redux state.
     * @param state Redux state
     * @param action PayloadAction<DynamicItem[]>
     */
    setTenantItems(state, action: PayloadAction<DynamicItem[]>) {
      state.tenantItems = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(createTenant.pending, (state, action) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(createTenant.fulfilled, (state, action) => {
      state.inProgress = false;
    });
    builder.addCase(createTenant.rejected, (state, action) => {
      state.inProgress = false;
      state.error =
        typeof action.payload === "string" ? action.payload : "Unknown error";
    });

    builder.addCase(getTenantDetailsThunk.pending, (state, action) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(getTenantDetailsThunk.fulfilled, (state, action) => {
      state.inProgress = false;
    });
    builder.addCase(getTenantDetailsThunk.rejected, (state, action) => {
      state.inProgress = false;
      state.error =
        typeof action.payload === "string" ? action.payload : "Unknown error";
    });

    builder
      .addCase(getTenantPetApplication.rejected, (state, action) => {
        state.error = typeof action.payload === "string" ? action.payload : "Unknown error";
      })
      .addCase(getTenantPetApplication.fulfilled, (state) => {
        state.error = null;
      });
  },
});

export const { setPrimartTenant, setStageOneData, setTenantDetails, setTenantSubmittedData,setAgentNameAndCompany, setIsValidAgent, setAgentId, setFormId, setApprovedPetsDetails, setTenantItems } = tenantSlice.actions;
export default tenantSlice.reducer;
